import { useEffect, useRef } from 'react';

type PollingCallback = () => Promise<boolean>;
type UsePollingOptions = {
  interval: number;
  timeout: number;
  onSuccess?: () => void;
  onTimeout?: () => void;
};

export function usePolling(
  shouldStart: boolean,
  callback: PollingCallback,
  options: UsePollingOptions,
) {
  const { interval, timeout, onSuccess, onTimeout } = options;
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const cancelled = useRef(false);
  const isPollingRef = useRef(false);

  const callbackRef = useRef(callback);
  const onSuccessRef = useRef(onSuccess);
  const onTimeoutRef = useRef(onTimeout);

  useEffect(() => {
    callbackRef.current = callback;
    onSuccessRef.current = onSuccess;
    onTimeoutRef.current = onTimeout;
  }, [callback, onSuccess, onTimeout]);

  useEffect(() => {
    if (!shouldStart) {
      if (isPollingRef.current) {
        cancelled.current = true;
        if (intervalRef.current) {
          clearTimeout(intervalRef.current);
          intervalRef.current = null;
        }
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        isPollingRef.current = false;
      }
      return;
    }

    if (isPollingRef.current) return;

    cancelled.current = false;
    isPollingRef.current = true;

    const startPolling = async () => {
      const poll = async () => {
        if (cancelled.current) {
          isPollingRef.current = false;
          return;
        }

        try {
          const success = await callbackRef.current();
          if (success) {
            isPollingRef.current = false;
            onSuccessRef.current?.();
            return;
          }
          if (!cancelled.current) {
            intervalRef.current = setTimeout(poll, interval);
          }
        } catch (error) {
          if (!cancelled.current) {
            intervalRef.current = setTimeout(poll, interval);
          }
        }
      };

      poll();

      timeoutRef.current = setTimeout(() => {
        if (!cancelled.current) {
          cancelled.current = true;
          isPollingRef.current = false;
          onTimeoutRef.current?.();
        }
      }, timeout);
    };

    startPolling();

    return () => {
      cancelled.current = true;
      isPollingRef.current = false;
      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
        intervalRef.current = null;
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [shouldStart, interval, timeout]);
}
